import { isMarketingConsentCountry, isSoftOptInCountry } from 'utils/GDPR.utils'
import { amplitude } from './amplitude'
import { brazeWhitelistAttributes } from './brazeWhitelistAttributes'
import { getEmailConsentBraze } from '../../../api/userCapturedData.api'
import { _EVENT_NAMES } from '../AnalyticsEvents'
import { logger } from '../../logger'

function setUserUnsubscribedWithReason(
  braze: any,
  user: any,
  reason: string,
  country: string,
  brazeExternalId: string | null,
  userId: string | null,
  email: string | null
): void {
  logger.info('Setting user as unsubscribed', {
    reason,
    country,
    brazeExternalId,
    userId,
    email,
  })

  user.setEmailNotificationSubscriptionType(
    braze.User.NotificationSubscriptionTypes.UNSUBSCRIBED
  )
  trackEmailSubscriptionStatusChange(
    braze.User.NotificationSubscriptionTypes.UNSUBSCRIBED,
    brazeExternalId,
    userId
  )
}

export async function setDefaultEmailSubscriptionByCountry(
  braze: any,
  email: string | null,
  brazeExternalId: string | null,
  userId: string | null,
  explicitEmailConsent: boolean | null = null
): Promise<void> {
  // Set default email subscription based on country
  // US and Australia: Keep default (subscribed) - no action needed
  // Other countries: Set to unsubscribed, but only if not already opted_in
  if (typeof window !== 'undefined' && window.KEYS?.ipCountry) {
    const userCountry = window.KEYS.ipCountry
    const user = braze.getUser()

    // Check if Braze user is available and warn if not
    if (!user) {
      logger.warning(
        'Braze user not available yet for setDefaultEmailSubscriptionByCountry',
        {
          userCountry,
          email: !!email,
          brazeExternalId: !!brazeExternalId,
          explicitEmailConsent,
          userId,
        }
      )
    } else {
      logger.info(
        'Braze user available for setDefaultEmailSubscriptionByCountry',
        {
          userCountry,
          email: !!email,
          brazeExternalId: !!brazeExternalId,
          explicitEmailConsent,
          userId,
        }
      )
    }

    if (
      isMarketingConsentCountry(userCountry) ||
      isSoftOptInCountry(userCountry)
    ) {
      logger.info('Setting default email subscription by country', {
        userCountry,
        email,
        brazeExternalId,
        explicitEmailConsent,
        userId,
      })
      // If explicit consent is provided, set to OPTED_IN directly
      if (explicitEmailConsent) {
        logger.info('Explicit consent provided, setting to OPTED_IN directly', {
          userCountry,
          email,
          brazeExternalId,
          explicitEmailConsent,
          userId,
        })
        return
      }

      // Otherwise, check existing consent and set to unsubscribed if needed
      if (email && brazeExternalId) {
        try {
          const currentConsent = await getEmailConsentBraze(brazeExternalId)
          logger.info('Current consent', {
            currentConsent,
            brazeExternalId,
            userId,
            email,
            userCountry,
          })

          if (
            currentConsent !==
              braze.User.NotificationSubscriptionTypes.OPTED_IN &&
            currentConsent !==
              braze.User.NotificationSubscriptionTypes.UNSUBSCRIBED
          ) {
            setUserUnsubscribedWithReason(
              braze,
              user,
              `Consent status: ${currentConsent}`,
              userCountry,
              brazeExternalId,
              userId,
              null
            )
          }
        } catch (error: any) {
          logger.error('Error setting default email subscription by country', {
            error,
            brazeExternalId,
            userId,
            email,
            userCountry,
          })
          setUserUnsubscribedWithReason(
            braze,
            user,
            error?.message || String(error),
            userCountry,
            brazeExternalId,
            userId,
            null
          )
        }
      } else {
        // When email/brazeExternalId are missing, check current status before changing
        // Only unsubscribe if user is in default "subscribed" state
        // Never change "opted_in" (explicit consent) or "unsubscribed" (already set)
        const currentStatus = user?.getEmailNotificationSubscriptionType?.()

        if (
          currentStatus === braze.User.NotificationSubscriptionTypes.SUBSCRIBED
        ) {
          logger.info(
            'Missing email or brazeExternalId, setting to unsubscribed (user was in default subscribed state)',
            {
              userCountry,
              email,
              brazeExternalId,
              explicitEmailConsent,
              userId,
              currentStatus,
            }
          )
          setUserUnsubscribedWithReason(
            braze,
            user,
            `Missing: email or brazeExternalId`,
            userCountry,
            brazeExternalId,
            userId,
            email
          )
        } else {
          logger.info(
            'Skipping unsubscribe - user already has explicit subscription status',
            {
              userCountry,
              email,
              brazeExternalId,
              explicitEmailConsent,
              userId,
              currentStatus,
            }
          )
        }
      }
    }
  }
}

export function trackEmailSubscriptionStatusChange(
  status: string,
  brazeExternalId: string | null = null,
  userId: string | null = null
): void {
  // Determine the most appropriate ID to use
  const id = brazeExternalId || userId || 'unknown'

  // Track the subscription status change
  logger.info('Tracking email subscription status change', {
    status,
    id,
    braze_external_id: brazeExternalId,
    user_id: userId,
  })

  amplitude.track(_EVENT_NAMES.EMAIL_SUBSCRIPTION_STATUS_CHANGED, {
    status,
    id,
    braze_external_id: brazeExternalId,
    user_id: userId,
  })
}

export function handleBrazeCustomAttributeArrays(
  braze: any,
  brazeEventName: string,
  eventProperties: Record<string, any>,
  eventID: string,
  eventName: string,
  platform: string
): void {
  // Get Braze user once for all operations in this function
  const user = braze.getUser()

  // Check if Braze user is available
  if (!user) {
    logger.warning(`Braze user not available for ${brazeEventName}`)
    return
  }

  if (brazeEventName === 'Web Email Marketing Consent Given') {
    // Set email subscription to opted_in when consent is given
    user.setEmailNotificationSubscriptionType(
      braze.User.NotificationSubscriptionTypes.OPTED_IN
    )
    trackEmailSubscriptionStatusChange(
      braze.User.NotificationSubscriptionTypes.OPTED_IN,
      null,
      null // We don't have userId in this context
    )

    const consentData = {
      $add: [
        {
          Source: eventProperties.Source,
          time: new Date().toISOString(),
        },
      ],
    }

    if (
      brazeWhitelistAttributes.includes('Web Email Marketing Consent Given')
    ) {
      user.setCustomUserAttribute(
        'Web Email Marketing Consent Given',
        consentData,
        true
      )
    }
  } else if (
    brazeEventName === 'Passed who is it for step' ||
    brazeEventName === 'Passed Who Is It For Step'
  ) {
    // Use Braze's $add operation to add to array of objects
    const whoIsItForData = {
      $add: [
        {
          value: eventProperties.value,
          time: new Date().toISOString(),
        },
      ],
    }

    if (brazeWhitelistAttributes.includes('Passed Who Is It For Step')) {
      user.setCustomUserAttribute(
        'Passed Who Is It For Step',
        whoIsItForData,
        true
      )
    }
  } else if (brazeEventName === _EVENT_NAMES.WEB_EMAIL_CAPTURED) {
    // Use Braze's $add operation to add to array of objects
    const emailCapturedData = {
      $add: [
        {
          Email: eventProperties.Email,
          'Onboarding Flow': eventProperties['Onboarding Flow'],
          'Is New Email': eventProperties['Is New Email'],
          time: new Date().toISOString(),
        },
      ],
    }

    if (brazeWhitelistAttributes.includes(_EVENT_NAMES.WEB_EMAIL_CAPTURED)) {
      user.setCustomUserAttribute(
        _EVENT_NAMES.WEB_EMAIL_CAPTURED,
        emailCapturedData,
        true
      )
    }
  } else if (brazeEventName === 'Photo Picked') {
    // Use Braze's $add operation to add to array of objects
    const photoPickedData = {
      $add: [
        {
          Context: eventProperties.context,
          'Tile Content': eventProperties['Tile Content'],
          'Picked Photos Count': eventProperties['Picked Photos Count'],
          time: new Date().toISOString(),
        },
      ],
    }

    if (brazeWhitelistAttributes.includes('Photo Picked')) {
      user.setCustomUserAttribute('Photo Picked', photoPickedData, true)
    }
  } else if (brazeEventName === _EVENT_NAMES.PHONE_NUMBER_CAPTURED) {
    // Use Braze's $add operation to add to array of objects
    const phoneNumberCapturedData = {
      $add: [
        {
          Source: eventProperties.Source,
          'Onboarding Flow': eventProperties['Onboarding Flow'],
          Platform: platform,
          time: new Date().toISOString(),
        },
      ],
    }

    if (brazeWhitelistAttributes.includes(_EVENT_NAMES.PHONE_NUMBER_CAPTURED)) {
      user.setCustomUserAttribute(
        _EVENT_NAMES.PHONE_NUMBER_CAPTURED,
        phoneNumberCapturedData,
        true
      )
    }
  } else if (brazeEventName === 'Phone Number Consent Given') {
    // Use Braze's $add operation to add to array of objects
    const phoneNumberConsentData = {
      $add: [
        {
          Source: eventProperties.Source,
          Platform: platform,
          time: new Date().toISOString(),
        },
      ],
    }

    if (brazeWhitelistAttributes.includes('Phone Number Consent Given')) {
      user.setCustomUserAttribute(
        'Phone Number Consent Given',
        phoneNumberConsentData,
        true
      )
    }
  } else if (brazeEventName === 'AI Artwork Generation Completed') {
    // Set AI results as a simple array of preview URLs
    if (
      eventProperties['AI Results'] &&
      brazeWhitelistAttributes.includes('AI Results')
    ) {
      user.setCustomUserAttribute('AI Results', eventProperties['AI Results'])
    }

    // Set AI Preview Results as array of framed preview URLs
    if (
      eventProperties['AI Preview Results'] &&
      brazeWhitelistAttributes.includes('AI Preview Results')
    ) {
      user.setCustomUserAttribute(
        'AI Preview Results',
        eventProperties['AI Preview Results']
      )
    }
  }

  // Always send as event for all cases
  braze.logCustomEvent(brazeEventName, {
    ...eventProperties,
    Platform: platform,
    $event_id: eventID,
    original_event_name: eventName,
  })
}

export function setBrazeUserProperties(
  braze: any,
  userProperties: Record<string, any>
): void {
  // Get Braze user once for all properties
  const brazeUser = braze.getUser()
  if (!brazeUser) {
    logger.warning('Braze user not available for setUserProperties (bulk)')
    return
  }

  Object.entries(userProperties).forEach(([key, value]) => {
    // Only send whitelisted attributes to Braze
    if (brazeWhitelistAttributes.includes(key)) {
      brazeUser.setCustomUserAttribute(key, value)
    }
  })
}
